/**
 * Authentication Form Validation Schemas
 *
 * Defines Zod schemas for all authentication forms:
 * - Sign up (email/password)
 * - Login (email/password)
 * - Magic link sign-in
 * - Forgot password request
 * - Password reset
 *
 * These schemas are used with react-hook-form for client-side validation
 * and ensure consistency across all auth forms.
 *
 * See: docs/v2/03-auth-and-security.md
 */

import { z } from 'zod';

/**
 * Validation schema for user sign-up
 *
 * Requirements:
 * - Email must be valid
 * - Password must be at least 8 characters
 * - Must contain uppercase, lowercase, and number
 * - Passwords must match
 */
export const signUpSchema = z
  .object({
    email: z
      .string()
      .min(1, 'Email is required')
      .email('Please enter a valid email address'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /[A-Z]/,
        'Password must contain at least one uppercase letter'
      )
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

/**
 * Type for sign-up form data
 */
export type SignUpFormData = z.infer<typeof signUpSchema>;

/**
 * Validation schema for user login
 *
 * Requirements:
 * - Email must be valid
 * - Password must be provided
 */
export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

/**
 * Type for login form data
 */
export type LoginFormData = z.infer<typeof loginSchema>;

/**
 * Validation schema for magic link sign-in
 *
 * Requirements:
 * - Email must be valid
 */
export const magicLinkSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
});

/**
 * Type for magic link form data
 */
export type MagicLinkFormData = z.infer<typeof magicLinkSchema>;

/**
 * Validation schema for forgot password request
 *
 * Requirements:
 * - Email must be valid
 */
export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
});

/**
 * Type for forgot password form data
 */
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

/**
 * Validation schema for password reset
 *
 * Requirements:
 * - New password must be at least 8 characters
 * - Must contain uppercase, lowercase, and number
 * - Confirm password must match
 */
export const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /[A-Z]/,
        'Password must contain at least one uppercase letter'
      )
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

/**
 * Type for reset password form data
 */
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
